import React from "react";
import { Badge } from "@/components/ui/badge";
import { Loader2, Award, Info } from "lucide-react";

export const GedPrepDisclaimer = ({ className = "", compact = false }) => (
  <div
    className={`flex items-start gap-2 rounded-xl border border-[#F2B705]/40 bg-[#FFF8DC] px-3 py-2 text-[13px] leading-snug text-[#5A4100] ${className}`}
    role="note"
    data-testid="ged-prep-disclaimer"
  >
    <Info className="h-4 w-4 shrink-0 mt-0.5 text-[#7A5A00]" aria-hidden="true" />
    <span>
      <span className="font-semibold">Prep & practice only.</span> Scholar Campus does not administer the official GED
      {compact ? "" : " exam, issue GED certificates, or report scores to GED Testing Service"}. To take the real GED, register at{" "}
      <a href="https://ged.com" target="_blank" rel="noreferrer" className="underline hover:text-[#5A4100]">ged.com</a>.
    </span>
  </div>
);

const GRADE_COLORS = {
  "Pre-K": "bg-[#FFF1C7] text-[#7A5A00] border-[#F2B705]/30",
  "K-2": "bg-[#FFE1E1] text-[#9B2C2C] border-[#F26B6B]/30",
  "3-5": "bg-[#DDF7E7] text-[#14663A] border-[#1F9D55]/30",
  "6-8": "bg-[#DCEBFF] text-[#1D4F91] border-[#2F80ED]/30",
  "9-12": "bg-[#D7F3F1] text-[#0B5B5B] border-[#14919B]/30",
  College: "bg-[#E9E1CF] text-[#4A3B1A] border-[#B9A57A]/40",
};

export const GradeBadge = ({ grade, className = "" }) => (
  <Badge variant="outline" className={`font-medium ${GRADE_COLORS[grade] || ""} ${className}`} data-testid="grade-badge">
    {grade}
  </Badge>
);

export const PageHeader = ({ eyebrow, title, subtitle, actions }) => (
  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-8 fade-up">
    <div>
      {eyebrow && <div className="text-xs font-display uppercase tracking-[0.14em] text-muted-foreground mb-2">{eyebrow}</div>}
      <h1 className="text-3xl sm:text-4xl font-display font-semibold tracking-tight text-[#071A22]">{title}</h1>
      {subtitle && <p className="mt-2 text-base text-muted-foreground max-w-2xl">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const StatCard = ({ icon: Icon, label, value, hint, tone = "teal", testid }) => {
  const tones = {
    teal: "bg-[#D7F3F1] text-[#0B6B6B]",
    amber: "bg-[#FFF1C7] text-[#7A5A00]",
    sky: "bg-[#DCEBFF] text-[#1D4F91]",
    green: "bg-[#DDF7E7] text-[#14663A]",
    coral: "bg-[#FFE1E1] text-[#9B2C2C]",
  };
  return (
    <div className="bg-card border border-border rounded-[14px] p-5 lift" data-testid={testid}>
      <div className="flex items-center gap-3">
        {Icon && (
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${tones[tone]}`}>
            <Icon className="h-5 w-5" />
          </div>
        )}
        <div className="text-sm text-muted-foreground">{label}</div>
      </div>
      <div className="mt-3 text-3xl font-display font-semibold text-[#071A22]">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
};

export const EmptyState = ({ icon: Icon, title, text, action, testid }) => (
  <div className="border border-dashed border-border rounded-[14px] bg-card p-8 text-center" data-testid={testid}>
    {Icon && (
      <div className="mx-auto h-12 w-12 rounded-2xl bg-secondary flex items-center justify-center mb-3">
        <Icon className="h-6 w-6 text-[#0F7B7D]" />
      </div>
    )}
    <div className="font-display font-semibold text-lg">{title}</div>
    {text && <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">{text}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const Loading = ({ label = "Loading..." }) => (
  <div className="flex items-center justify-center py-20 text-muted-foreground gap-2" data-testid="loading-indicator">
    <Loader2 className="h-5 w-5 animate-spin" /> {label}
  </div>
);

export const SectionTitle = ({ children, action }) => (
  <div className="flex items-center justify-between mb-3">
    <h3 className="text-sm font-display uppercase tracking-[0.14em] text-muted-foreground">{children}</h3>
    {action}
  </div>
);

export const scoreTone = (p) => (p == null ? "text-muted-foreground" : p >= 80 ? "text-[#1F9D55]" : p >= 60 ? "text-[#B58900]" : "text-[#D64545]");

export const fmtDate = (s) => {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return s;
  }
};

// Board / exam-prep courses: honest "aligned" label + non-affiliation note.
export const FrameworkBadge = ({ item, className = "" }) => {
  if (!item || !item.collection || item.collection === "Core") return null;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium text-[#7A4B00] bg-[#FFF1D6] border border-[#F3D9A4] rounded-full px-2 py-0.5 ${className}`}
      title={item.framework || ""}
      data-testid="framework-badge"
    >
      <Award className="h-3 w-3" /> Aligned to the official exam framework
    </span>
  );
};

export const FrameworkNote = ({ item, className = "" }) => {
  if (!item || !item.collection || item.collection === "Core") return null;
  return (
    <div className={`rounded-xl border border-[#F3D9A4] bg-[#FFF8EA] p-4 text-sm text-[#5C3B00] ${className}`} data-testid="framework-note">
      <div className="flex items-center gap-2 font-display font-semibold text-[#7A4B00]"><Award className="h-4 w-4" /> Aligned to the official exam framework</div>
      {item.framework && <div className="mt-1">Follows: <span className="font-medium">{item.framework}</span></div>}
      <div className="mt-1 text-xs text-[#7A5A20]">
        {item.exam_body ? `${item.exam_body.charAt(0).toUpperCase()}${item.exam_body.slice(1)} is` : "The exam body is"} not affiliated with and does not endorse Scholar Campus.
        {item.exam ? ` The official ${item.exam} is taken separately.` : " The official exam is taken separately."}
      </div>
    </div>
  );
};
